import { baseSportsApi, API_URL } from '../utils/sportsApi';
import { parseStartDate, parseEndDate } from '../utils/parseDate';
import { rhAttributionPostQueryFields } from './rhAttributionPostQueryFields';

// Constants
const IMAGE_TYPE_ID = 1;
const platformVideos = [
    'YouTube',
    'Facebook',
    'Instagram',
    'Twitter',
    'X',
    'TikTok',
    'Website',
    'App',
];

const platformImages = ['YouTube', 'Facebook', 'Instagram', 'Website', 'App'];

export const getFetchPostPayload = ({
    startDate,
    endDate,
    rightsholders,
    rhWithSP,
    sponsors,
    rightsholderPlacementIds,
    sortBy,
    client,
    rightsholderAttribution,
}) => {
    const startDateFormatted = startDate && parseStartDate(startDate);
    const endDateFormatted = endDate && parseEndDate(endDate);
    const rightsholderIds = (rhWithSP ?? []).filter(
        rhId => !!(rightsholders ?? {})[rhId],
    );

    const domainObject = {
        rightsholderIds,
        rightsholderPlacementIds,
        sponsorIds: sponsors,
        orderBy: [`${sortBy}-desc`],
        startDate: startDate ? startDateFormatted : null,
        endDate: endDate ? endDateFormatted : null,
        includeTextTypeExposures: true,
        pageSize: 50,
        mediaTypeIds: [IMAGE_TYPE_ID],
        ...rhAttributionPostQueryFields(client, rightsholderAttribution),
    };

    return {
        action: 'fetchQuery',
        domainObject,
    };
};

export const getMediaCheckPostPayload = ({
    rightsholderIds,
    sponsors,
    rightsholderPlacementIds,
    startDate,
    endDate,
    client,
    rightsholderAttribution,
    broadcastNetworkCountryIds = [],
}) => {
    const startDateFormatted = startDate && parseStartDate(startDate);
    const endDateFormatted = endDate && parseEndDate(endDate);

    const domainObject = {
        rightsholderIds,
        sponsorIds: sponsors,
        rightsholderPlacementIds,
        includeTextTypeExposures: true,
        startDate: startDate ? startDateFormatted : null,
        endDate: endDate ? endDateFormatted : null,
        broadcastNetworkCountryIds,
        ...rhAttributionPostQueryFields(client, rightsholderAttribution),
    };

    return {
        action: 'mediaCheck',
        domainObject,
    };
};

export const fetchPosts = async ({
    startDate,
    endDate,
    rightsholders,
    rhWithSP,
    sponsors,
    placement,
    sortBy = 'sponsorMediaValue',
    onTimeout,
    defaultOnError,
    setIsLoadingPosts,
    filteredPlacements,
    clearAllNotifs = undefined,
    onStartFetchingPosts = undefined,
    signal = undefined,
    client,
    rightsholderAttribution,
    broadcastNetworkCountryIds = [],
}) => {
    const rightsholderIds = (rhWithSP ?? []).filter(
        rhId => !!(rightsholders ?? {})[rhId],
    );

    const rightsholderPlacementIds =
        placement && placement !== 'false'
            ? (filteredPlacements ?? []).filter(id =>
                  id.endsWith(`-${placement}`),
              )
            : filteredPlacements ?? [];

    setIsLoadingPosts?.(true);
    if (onStartFetchingPosts) onStartFetchingPosts();
    try {
        const [rawPosts, availableMedia] = await Promise.all([
            baseSportsApi
                .url(API_URL + '/social-posts')
                .options(signal ? { signal } : {})
                .json(
                    getFetchPostPayload({
                        startDate,
                        endDate,
                        rightsholders,
                        rhWithSP,
                        sponsors,
                        rightsholderPlacementIds,
                        sortBy,
                        client,
                        rightsholderAttribution,
                    }),
                )
                .post()
                .json(),

            baseSportsApi
                .url(API_URL + '/social-posts')
                .options(signal ? { signal } : {})
                .json(
                    getMediaCheckPostPayload({
                        rightsholderIds,
                        sponsors,
                        rightsholderPlacementIds,
                        startDate,
                        endDate,
                        client,
                        rightsholderAttribution,
                        broadcastNetworkCountryIds,
                    }),
                )
                .post()
                .json(),
        ]);

        const posts = rawPosts?.posts ?? [];
        const { videos = [], images = [] } = availableMedia ?? {};

        const hasVideos = videos.some(
            v =>
                platformVideos.findIndex(
                    name => v.toLowerCase() == name.toLowerCase(),
                ) > -1,
        );

        const hasImages = images.some(
            i =>
                platformImages.findIndex(
                    name => i.toLowerCase() == name.toLowerCase(),
                ) > -1,
        );

        const hasBroadcast = videos.findIndex(v => v == 'Broadcast') > -1;

        return {
            posts,
            availableMedia: {
                video: hasVideos,
                image: hasImages,
                broadcast: hasBroadcast,
            },
        };
    } catch (e) {
        if (e.status === 504 || e.status === 408) {
            onTimeout?.();
        } else {
            defaultOnError?.(e.message);
        }
    } finally {
        setIsLoadingPosts?.(false);
        if (clearAllNotifs) clearAllNotifs();
    }

    return { posts: [], availableMedia: {} };
};
